import { useEffect, useState } from "react";
import { useSession } from "@/auth/session";
import {
  createPaymentMethod,
  deletePaymentMethod,
  listPaymentMethods,
  type PaymentMethodItem,
  updatePaymentMethod,
} from "@/lib/api";

type FormState = {
  code: string;
  name: string;
  isCredit: boolean;
  status: "active" | "inactive";
  manualEnabled: boolean;
  onlineEnabled: boolean;
  requiresBackup: boolean;
};

const emptyForm: FormState = {
  code: "",
  name: "",
  isCredit: false,
  status: "active",
  manualEnabled: true,
  onlineEnabled: false,
  requiresBackup: false,
};

export default function FormasPagoTab() {
  const { session } = useSession();
  const [items, setItems] = useState<PaymentMethodItem[]>([]);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      if (!session) return;
      setLoading(true);
      setError(null);
      try {
        const rows = await listPaymentMethods(session);
        if (!cancelled) setItems(rows);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "No se pudieron cargar formas de pago");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [session]);

  function resetForm(): void {
    setEditingId(null);
    setForm(emptyForm);
  }

  function openCreateModal(): void {
    resetForm();
    setShowCreateModal(true);
  }

  function closeCreateModal(): void {
    setShowCreateModal(false);
    resetForm();
  }

  function editItem(item: PaymentMethodItem): void {
    setEditingId(item.id);
    setForm({
      code: item.code,
      name: item.name,
      isCredit: item.isCredit,
      status: item.status === "inactive" ? "inactive" : "active",
      manualEnabled: item.manualEnabled,
      onlineEnabled: item.onlineEnabled,
      requiresBackup: item.requiresBackup,
    });
  }

  async function onSave(): Promise<void> {
    if (!session || !form.code.trim() || !form.name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        code: form.code.trim().toLowerCase(),
        name: form.name.trim(),
        isCredit: form.isCredit,
        status: form.status,
        manualEnabled: form.manualEnabled,
        onlineEnabled: form.onlineEnabled,
        requiresBackup: form.requiresBackup,
      };
      if (editingId) {
        const updated = await updatePaymentMethod(session, editingId, payload);
        setItems((prev) => prev.map((row) => (row.id === editingId ? updated : row)));
        resetForm();
      } else {
        const created = await createPaymentMethod(session, payload);
        setItems((prev) => [created, ...prev]);
        closeCreateModal();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo guardar forma de pago");
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(id: string): Promise<void> {
    if (!session || !window.confirm("¿Eliminar forma de pago?")) return;
    try {
      await deletePaymentMethod(session, id);
      setItems((prev) => prev.filter((row) => row.id !== id));
      if (editingId === id) resetForm();
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo eliminar forma de pago");
    }
  }

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-800">Formas de pago ({items.length})</h2>
          <button onClick={openCreateModal} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
            <i className="ri-add-line" />
            Nueva forma de pago
          </button>
        </div>

        {loading ? (
          <div className="h-16 bg-gray-100 rounded animate-pulse" />
        ) : (
          <div className="space-y-2">
            {items.length === 0 ? (
              <p className="text-sm text-gray-500">Sin formas de pago registradas.</p>
            ) : (
              items.map((item) => (
                <div key={item.id} className="flex items-center justify-between border border-gray-100 rounded-lg px-3 py-2">
                  <div>
                    <div className="text-sm font-medium text-gray-800">{item.name}</div>
                    <div className="text-xs text-gray-500 font-mono">{item.code}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${item.isCredit ? "bg-amber-50 text-amber-700" : "bg-blue-50 text-blue-700"}`}>
                      {item.isCredit ? "Crédito" : "Contado"}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full ${item.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>
                      {item.status === "active" ? "Activo" : "Inactivo"}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full ${item.manualEnabled ? "bg-indigo-50 text-indigo-700" : "bg-gray-100 text-gray-500"}`}>
                      {item.manualEnabled ? "Pago en caja" : "Sin caja"}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full ${item.onlineEnabled ? "bg-cyan-50 text-cyan-700" : "bg-gray-100 text-gray-500"}`}>
                      {item.onlineEnabled ? "Pago online" : "Sin online"}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full ${item.requiresBackup ? "bg-violet-50 text-violet-700" : "bg-gray-100 text-gray-500"}`}>
                      {item.requiresBackup ? "Exige respaldo" : "Sin respaldo"}
                    </span>
                    <button onClick={() => editItem(item)} className="text-xs px-2 py-1 border border-gray-200 rounded">Editar</button>
                    <button onClick={() => void onDelete(item.id)} className="text-xs px-2 py-1 border border-red-200 text-red-600 rounded">Eliminar</button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {editingId ? (
          <div className="mt-4 border border-gray-100 rounded-lg p-4">
            <h3 className="text-xs font-semibold text-gray-700 mb-2">Editar forma de pago</h3>
            <div className="grid grid-cols-4 gap-3">
              <input value={form.code} onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value.toLowerCase() }))} className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
              <input value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
              <select value={form.status} onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value as "active" | "inactive" }))} className="px-3 py-2 text-sm border border-gray-200 rounded-lg">
                <option value="active">Activo</option>
                <option value="inactive">Inactivo</option>
              </select>
              <button onClick={() => void onSave()} disabled={saving || !form.code.trim() || !form.name.trim()} className="px-3 py-2 text-sm text-white bg-blue-600 rounded-lg disabled:opacity-60">
                {saving ? "Guardando..." : "Actualizar"}
              </button>
            </div>
            <label className="mt-3 inline-flex items-center gap-2 text-xs text-gray-700">
              <input type="checkbox" checked={form.isCredit} onChange={(e) => setForm((prev) => ({ ...prev, isCredit: e.target.checked }))} />
              Es crédito
            </label>
            <label className="mt-3 ml-3 inline-flex items-center gap-2 text-xs text-gray-700">
              <input type="checkbox" checked={form.manualEnabled} onChange={(e) => setForm((prev) => ({ ...prev, manualEnabled: e.target.checked }))} />
              Pago en caja
            </label>
            <label className="mt-3 ml-3 inline-flex items-center gap-2 text-xs text-gray-700">
              <input type="checkbox" checked={form.onlineEnabled} onChange={(e) => setForm((prev) => ({ ...prev, onlineEnabled: e.target.checked }))} />
              Pago online
            </label>
            <label className="mt-3 ml-3 inline-flex items-center gap-2 text-xs text-gray-700">
              <input type="checkbox" checked={form.requiresBackup} onChange={(e) => setForm((prev) => ({ ...prev, requiresBackup: e.target.checked }))} />
              Exige respaldo
            </label>
            <button onClick={resetForm} className="mt-3 ml-3 text-xs px-3 py-1.5 border border-gray-200 rounded-lg">Cancelar edición</button>
          </div>
        ) : null}

        {error && <p className="mt-3 text-xs text-red-600">{error}</p>}
      </div>

      {showCreateModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-xl border border-gray-100 w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800">Nueva forma de pago</h3>
              <button onClick={closeCreateModal} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400">
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="space-y-3">
              <input value={form.code} onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value.toLowerCase() }))} placeholder="Código (ej: transferencia)" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
              <input value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} placeholder="Nombre forma de pago" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
              <select value={form.status} onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value as "active" | "inactive" }))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg">
                <option value="active">Activo</option>
                <option value="inactive">Inactivo</option>
              </select>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={form.isCredit} onChange={(e) => setForm((prev) => ({ ...prev, isCredit: e.target.checked }))} />
                Es crédito
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={form.manualEnabled} onChange={(e) => setForm((prev) => ({ ...prev, manualEnabled: e.target.checked }))} />
                Pago en caja
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={form.onlineEnabled} onChange={(e) => setForm((prev) => ({ ...prev, onlineEnabled: e.target.checked }))} />
                Pago online
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={form.requiresBackup} onChange={(e) => setForm((prev) => ({ ...prev, requiresBackup: e.target.checked }))} />
                Exige respaldo
              </label>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={closeCreateModal} className="flex-1 px-4 py-2 text-sm border border-gray-200 rounded-lg">Cancelar</button>
              <button onClick={() => void onSave()} disabled={saving || !form.code.trim() || !form.name.trim()} className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg disabled:opacity-60">
                {saving ? "Creando..." : "Crear forma de pago"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
